import { resolveMemberMediaUrl } from '@formosa/shared/utils/mediaUrl';
import { normalizeDiscoveryItem } from './exploreModel';

export const DISCOVER_TABS = Object.freeze(['for-you', 'following', 'map', 'journeys', 'social']);
export const DEFAULT_DISCOVER_TAB = 'for-you';
export const JOURNEY_DISCOVERY_SORTS = Object.freeze(['LATEST', 'OLDEST', 'HOT', 'LIKES', 'COMMENTS', 'COPIES']);
export const FEED_CONTENT_TYPES = Object.freeze({
  JOURNEY_PUBLICATION: 'JOURNEY_PUBLICATION',
  SOCIAL_POST: 'SOCIAL_POST',
});

/**
 * 解析 Explore tab。
 * IA v2 secondary：for-you／following／map。
 * Compatibility：journeys／social（含 view=map → map）仍可深連結。
 */
export function readDiscoverTab(searchParams) {
  const raw = String(searchParams?.get?.('tab') || '').trim().toLowerCase();
  if (raw === 'following' || raw === 'follow') return 'following';
  if (raw === 'map') return 'map';
  if (raw === 'journeys' || raw === 'journey' || raw === 'search') return 'journeys';
  // social 深連結（含 view=map）保留在 posts panel，避免切 tab 導致 selection 丟失
  if (raw === 'social' || raw === 'explore') return 'social';
  if (raw === 'for-you' || raw === 'foryou' || raw === 'feed') return 'for-you';
  return DEFAULT_DISCOVER_TAB;
}

function snowflakeId(value) {
  if (value == null || value === '') return null;
  const id = String(value);
  return /^[1-9]\d*$/.test(id) ? id : null;
}

function parseJsonObject(raw) {
  if (raw == null || raw === '') return null;
  if (typeof raw === 'object') return raw;
  try {
    const parsed = JSON.parse(String(raw));
    return parsed && typeof parsed === 'object' ? parsed : null;
  } catch {
    return null;
  }
}

function destinationLabel(destinationSummaryJson) {
  const parsed = parseJsonObject(destinationSummaryJson);
  if (!parsed) return '';
  return String(parsed.displayName || parsed.canonicalName || parsed.name || parsed.summary || '').trim();
}

function budgetLabel(budgetSummaryJson) {
  const parsed = parseJsonObject(budgetSummaryJson);
  if (!parsed) return '';
  const amount = parsed.amount ?? parsed.total ?? parsed.estimatedAmount;
  const currency = parsed.currency || parsed.currencyCode || '';
  if (amount == null || amount === '') return currency ? String(currency) : '';
  return `${amount}${currency ? ` ${currency}` : ''}`.trim();
}

/**
 * 列表故事訊號：只接受安全範圍內的正整數；0、null、缺欄位、字串、小數、負數、NaN、Infinity 一律為 null（不顯示入口）。
 * 不做字串轉換，避免把非數字的值誤當成有故事。
 */
export function normalizeStoryCardCount(value) {
  return Number.isSafeInteger(value) && value > 0 ? value : null;
}

export function normalizeForYouItem(raw) {
  const contentType = String(raw?.contentType || '').trim();
  const contentId = snowflakeId(raw?.contentId);
  if (!contentId || (contentType !== FEED_CONTENT_TYPES.JOURNEY_PUBLICATION && contentType !== FEED_CONTENT_TYPES.SOCIAL_POST)) {
    return null;
  }
  const title = String(raw?.title || '').trim();
  const summary = String(raw?.summary || '').trim();
  const coverFileReference = String(raw?.coverFileReference || '').trim();
  const creatorMemberId = snowflakeId(raw?.creatorMemberId);
  const linkedJourneyPublicationId = snowflakeId(raw?.linkedJourneyPublicationId);
  const itemKey = `${contentType}:${contentId}`;
  return {
    itemKey,
    contentType,
    contentId,
    creatorMemberId,
    title: title || (contentType === FEED_CONTENT_TYPES.SOCIAL_POST ? 'Travel story' : 'Public journey'),
    summary,
    coverUrl: resolveMemberMediaUrl(coverFileReference),
    destinationLabel: destinationLabel(raw?.destinationSummaryJson),
    dayCount: Number.isFinite(Number(raw?.dayCount)) ? Number(raw.dayCount) : null,
    budgetLabel: budgetLabel(raw?.budgetSummaryJson),
    publishedAt: raw?.publishedAt || null,
    postKind: raw?.postKind || null,
    linkedJourneyPublicationId,
    deliveryType: raw?.deliveryType || null,
    // 只有 JOURNEY_PUBLICATION（含贊助）可能帶故事卡數；SOCIAL_POST 一律不顯示故事入口
    storyCardCount: contentType === FEED_CONTENT_TYPES.JOURNEY_PUBLICATION
      ? normalizeStoryCardCount(raw?.storyCardCount)
      : null,
    // 只有 SOCIAL_POST 會帶 canonical 投影；Journey 推薦不得出現 engagement
    socialPost: contentType === FEED_CONTENT_TYPES.SOCIAL_POST && raw?.socialPost
      ? normalizeDiscoveryItem(raw.socialPost)
      : null,
  };
}

export function normalizeForYouPage(payload) {
  return {
    feedSessionId: payload?.feedSessionId || null,
    items: Array.isArray(payload?.items)
      ? payload.items.map(normalizeForYouItem).filter(Boolean)
      : [],
    nextCursor: payload?.nextCursor || null,
    hasMore: Boolean(payload?.hasMore),
  };
}

/** Hide NI / mute on own For You content (viewer === creator). */
export function canShowFeedPreferenceActions({ viewerMemberId, creatorMemberId } = {}) {
  const viewer = snowflakeId(viewerMemberId);
  const creator = snowflakeId(creatorMemberId);
  if (!viewer || !creator) return true;
  return viewer !== creator;
}

export function normalizeJourneySearchItem(raw) {
  const publicationId = snowflakeId(raw?.publicationId);
  if (!publicationId) return null;
  return {
    publicationId,
    creatorMemberId: snowflakeId(raw?.creatorMemberId),
    title: String(raw?.title || '').trim() || 'Public journey',
    summary: String(raw?.summary || '').trim(),
    coverUrl: resolveMemberMediaUrl(raw?.coverFileReference),
    destinationLabel: destinationLabel(raw?.destinationSummaryJson),
    budgetLabel: budgetLabel(raw?.budgetSummaryJson),
    dayCount: Number.isFinite(Number(raw?.dayCount)) ? Number(raw.dayCount) : null,
    publishedAt: raw?.publishedAt || null,
    matchedLocationId: snowflakeId(raw?.matchedLocationId),
    storyCardCount: normalizeStoryCardCount(raw?.storyCardCount),
    storyKnownEmpty: raw?.storyCardCount === 0,
    likeCount: Number.isSafeInteger(raw?.likeCount) && raw.likeCount >= 0 ? raw.likeCount : null,
    commentCount: Number.isSafeInteger(raw?.commentCount) && raw.commentCount >= 0 ? raw.commentCount : null,
    organicCopyCount: Number.isSafeInteger(raw?.organicCopyCount) && raw.organicCopyCount >= 0 ? raw.organicCopyCount : null,
  };
}

/**
 * Response-level keyset emitted by the backend. It survives a page whose rows were all filtered
 * out by moderation, which is exactly when the last-item fallback cannot produce a cursor.
 */
function responseLevelCursor(payload, sort) {
  const publicationId = snowflakeId(payload?.nextCursorPublicationId);
  if (!publicationId) return null;
  if (['LIKES', 'COMMENTS', 'COPIES'].includes(sort)) {
    if (payload?.nextCursorEngagementCount == null) return null;
    return { cursorEngagementCount: payload.nextCursorEngagementCount, cursorPublicationId: publicationId };
  }
  if (sort === 'HOT') {
    if (payload?.nextCursorHotScore == null) return null;
    return {
      cursorHotScore: payload.nextCursorHotScore,
      cursorPublicationId: publicationId,
    };
  }
  if (payload?.nextCursorPublishedAt == null || payload.nextCursorPublishedAt === '') return null;
  return {
    cursorPublishedAt: payload.nextCursorPublishedAt,
    cursorPublicationId: publicationId,
  };
}

function lastItemCursor(payload, sort) {
  const items = Array.isArray(payload?.items) ? payload.items : [];
  const last = items[items.length - 1];
  const publicationId = snowflakeId(last?.publicationId);
  if (!publicationId) return null;
  if (['LIKES', 'COMMENTS', 'COPIES'].includes(sort)) {
    const field = { LIKES: 'likeCount', COMMENTS: 'commentCount', COPIES: 'organicCopyCount' }[sort];
    if (!Number.isSafeInteger(last[field]) || last[field] < 0) return null;
    return { cursorEngagementCount: last[field], cursorPublicationId: publicationId };
  }
  if (sort === 'HOT') {
    return {
      cursorHotScore: last.hotScore != null ? last.hotScore : undefined,
      cursorPublicationId: publicationId,
    };
  }
  return {
    cursorPublishedAt: last.publishedAt || undefined,
    cursorPublicationId: publicationId,
  };
}

/** Build next Journey Search keyset from raw API page (preserves hotScore for HOT sort). */
export function journeySearchNextCursor(rawPayload, sort) {
  if (!rawPayload?.hasMore) return null;
  // Only fall back to the last row when the backend did not send its own keyset (older builds).
  return responseLevelCursor(rawPayload, sort) || lastItemCursor(rawPayload, sort);
}

export function normalizeJourneySearchPage(payload, sort) {
  return {
    items: Array.isArray(payload?.items)
      ? payload.items.map(normalizeJourneySearchItem).filter(Boolean)
      : [],
    hasMore: Boolean(payload?.hasMore),
    nextCursor: journeySearchNextCursor(payload, sort),
  };
}
